import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { zusToast } from '@zep/toast';
import { useViewport } from '@/hooks/useViewport';
import { useCodeInputHandlers } from '@/shared/hooks';
import { normalizeEntryCode } from '@/shared/utils';
import { Button, TextField } from '@/ui_v3';

// 학생 코드는 U-prefix + 7자 = 8자 고정.
const CODE_MAX_LENGTH = 8;
type Props = {
  onVerify: (code: string) => void;
  isPending: boolean;
  defaultCode?: string;
};
export function CodeVerifyForm({
  onVerify,
  isPending,
  defaultCode
}: Props) {
  const {
    t
  } = useTranslation('classroom');
  const [code, setCode] = useState(() => normalizeEntryCode(defaultCode ?? '', CODE_MAX_LENGTH));
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    isMobile
  } = useViewport();
  const didFocusRef = useRef(false);
  // 입력 위생(공백·특수 제거·대문자·8자 cap)은 /join 과 공유. IME 조합은 가드된다.
  const codeInputHandlers = useCodeInputHandlers(setCode, CODE_MAX_LENGTH);

  // 진입 시 1회 자동 포커스 — 비모바일만(모바일은 키보드 자동 노출 방지).
  useEffect(() => {
    if (didFocusRef.current || isMobile) return;
    didFocusRef.current = true;
    inputRef.current?.focus();
  }, [isMobile]);
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = normalizeEntryCode(code, CODE_MAX_LENGTH);
    if (!normalized) {
      zusToast.error(t('codeLogin.verifyInvalid'), {
        position: 'top-right'
      });
      return;
    }
    onVerify(normalized);
  };
  return <form onSubmit={handleSubmit} className="flex w-full flex-col gap-md" data-sentry-component="CodeVerifyForm" data-sentry-source-file="CodeVerifyForm.tsx">
      <TextField ref={inputRef} title={t('codeLogin.codeTitle')} required placeholder={t('codeLogin.codePlaceholder')} inputMode="text" autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false} value={code} {...codeInputHandlers} data-sentry-element="TextField" data-sentry-source-file="CodeVerifyForm.tsx" />
      <Button type="submit" variant="contained" intent="strong" size="lg" disabled={isPending || !code} className="w-full" data-sentry-element="Button" data-sentry-source-file="CodeVerifyForm.tsx">
        {t('codeLogin.submit')}
      </Button>
    </form>;
}